import * as React from 'react';
import { useEffect, useState } from 'react';
import { TransProps, useTranslation } from 'react-i18next';

import { useParsers } from '../../hooks';
import { RadioOption, UseParsers } from '../../interfaces';
import { UseState } from '../../types';
import { Input } from '../input/input.component';

import {
  Container, 
  ImageElement, 
  ItemDetailsContainer, 
  Price, 
  ShippingOptionsContainer,
  StyledBoxTitle,
  StyledRadioButton
} from './itemShippingSettings.styled';
import { ItemShippingSettingsProps } from './itemShippingSettings.types';

export const ItemShippingSettings: React.FC<ItemShippingSettingsProps> = (props: ItemShippingSettingsProps): JSX.Element => {
  const {
    itemDetails,
    shippingOptions,
    onChange,
    currency = 'zł',
    className,
    name,
    initialRadioValue,
    initialInputValue
  }: ItemShippingSettingsProps = props;
  const { t }: TransProps<never> = useTranslation();
  const { parsePrice }: UseParsers = useParsers();
  const [activeRadio, setActiveRadio]: UseState<string | null> = useState<string | null>(initialRadioValue ?? null);
  const [inputValue, setInputValue]: UseState<string> = useState<string>(initialInputValue ?? '');

  const onChangeAction: (optionName: string) => void = (optionName: string): void => {
    onChange(optionName, inputValue);
    setActiveRadio(optionName);
  };

  const onInputBlur: (value: string) => void = (value: string): void => {
    setInputValue(value);
    if (value && activeRadio) {
      onChange(activeRadio, value);
    }
  };

  useEffect(
    (): void => {
      if (initialRadioValue && initialRadioValue !== activeRadio) {
        setActiveRadio(initialRadioValue);
      }
      if (initialInputValue && initialInputValue !== inputValue) {
        setInputValue(initialInputValue);
      }
    },
    [initialRadioValue, initialInputValue]
  );

  return (
    <Container className={className}>
      <ItemDetailsContainer>
        <ImageElement src={itemDetails.image} alt={itemDetails.boxTitleDetails.author} />
        <StyledBoxTitle boxTitleDetails={itemDetails.boxTitleDetails} />
        <Price>{parsePrice(itemDetails.price)} {currency}</Price>
      </ItemDetailsContainer>
      <ShippingOptionsContainer>
        {shippingOptions.map((option: RadioOption): JSX.Element => (
          <StyledRadioButton 
            label={option.label} 
            isActive={option.optionName === activeRadio} 
            onChange={(): void => onChangeAction(option.optionName ?? '')}
            additionalAttribute={option.additionalAttribute} 
            optionName={option.optionName}
            key={option.label}
            name={name ?? option.optionName}
          />
        ))}
      </ShippingOptionsContainer>
      <Input 
        label={t('onarte.ui.itemShippingSettings.input.label')} 
        onBlur={onInputBlur} 
        data-testid='input' 
        value={inputValue}
      />
    </Container>
  );
};
